import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Target } from 'lucide-react';
import { Button } from '@command-center/ui-system';
import { cascadeStatusLabel, isOpenCascadeStatus } from '@command-center/leadloop-core';
import { leadloopApi, type ActivityItem } from '../lib/leadloop-api';
import { ErrorState, Field, LeadStatusBadge, LoadingState, LoopReasonBadge, VerifiedTag, formatDateTime, loopActionLabel } from './parts';
import { EmailLink, SendEmailDialog } from './SendEmailDialog';
import { LeadActionCenter } from '../components/lead-action-center';
import { DetailSection, RecordDetailHeading, RecordDetailShell, RecordTabs } from '@command-center/ui-system';
import { ActivityTimeline, DetailTabBody, LinkContactDialog, NotesPanel, RelatedRecordList } from '../components/record-detail-panels';
import { LinkedIntakeRecords } from '../components/linked-intake-records';

export function LeadDetailView({
  id,
  onClose,
  onPrevious,
  onNext,
  embedded = false,
}: {
  id: string;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  embedded?: boolean;
}) {
  const [tab, setTab] = useState('overview');
  const [reopenBusy, setReopenBusy] = useState(false);
  const queryClient = useQueryClient();
  useEffect(() => setTab('overview'), [id]);
  const detail = useQuery({
    queryKey: ['lead', id],
    queryFn: () => leadloopApi.leadDetail(id),
    enabled: Boolean(id),
  });
  const activity = useQuery({
    queryKey: ['activity', id],
    queryFn: () => leadloopApi.activity({ entityId: id, limit: 100 }),
    enabled: Boolean(id),
  });
  const loop = useQuery({ queryKey: ['loop'], queryFn: leadloopApi.loop });

  if (detail.isLoading) return <LoadingState />;
  if (detail.isError) return <ErrorState error={detail.error} />;
  if (!detail.data) return null;
  const { lead, contact, organization, intake } = detail.data;
  const notes = (activity.data?.items ?? []).filter(isNoteEntry);
  const loopItem = loop.data?.items.find((item) => item.lead.id === lead.id);
  const reopen = async () => {
    setReopenBusy(true);
    try {
      await leadloopApi.archiveLead(lead.id, false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['lead', id] }),
        queryClient.invalidateQueries({ queryKey: ['leads'] }),
        queryClient.invalidateQueries({ queryKey: ['loop'] }),
        queryClient.invalidateQueries({ queryKey: ['activity', id] }),
      ]);
    } finally {
      setReopenBusy(false);
    }
  };

  return (
    /*
      One title, one place. The shell's nav bar no longer repeats it and the
      body no longer opens with it — the heading below is the only place this
      Lead is named.
    */
    <RecordDetailShell embedded={embedded} recordType="lead" onClose={onClose} onPrevious={onPrevious} onNext={onNext}>
      <RecordDetailHeading
        icon={Target}
        title={lead.title}
        badges={<><LeadStatusBadge status={lead.status} /><LoopReasonBadge reason={loopItem?.reason} /><VerifiedTag verified={lead.verified} /></>}
        /*
          Drafting an email is the one action that leaves this record, so it is
          the one action in the header. Everything a user does *to* the lead —
          the note, the follow-up date, verifying it — happens in the Action
          Center below, and a second entry point to any of it up here would
          split one interaction across two places. Archive is deliberately not
          here either; it lives at the bottom of the Overview.
        */
        actions={<SendEmailDialog leadId={lead.id} />}
      />
      <RecordTabs active={tab} onChange={setTab} tabs={[
        { id: 'overview', label: 'Overview' },
        { id: 'linked', label: 'Linked records', count: (contact ? 1 : 0) + (organization ? 1 : 0) + intake.length },
        { id: 'notes', label: 'Notes' },
        { id: 'activity', label: 'Activity' },
      ]} />
      <DetailTabBody>
        {tab === 'overview' && <>
          {/*
            Start with the customer's words. The title is only a generated
            summary; the original request is the context a reviewer needs
            before deciding what to write or do in the Action Center.
          */}
          {lead.message && (
            <DetailSection title="Original request">
              <p className="whitespace-pre-wrap rounded-md border border-border bg-muted/50 p-3 text-sm leading-relaxed text-foreground">
                {lead.message}
              </p>
            </DetailSection>
          )}
          {/*
            The Action Center leads the record, in or out of the queue. A lead
            that is not due today still gets notes written on it, and hiding the
            only place to write one behind "is this in the feed?" is what sent
            people back to the modal.
          */}
          <div className="p-4 pb-0">
            <LeadActionCenter lead={lead} recommendation={loopItem ? loopActionLabel(loopItem.reason) : undefined} />
          </div>
          <DetailSection title="Lead details">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-3">
              <Field label="Contact" value={lead.contactName} />
              <Field label="Company" value={lead.companyName} />
              <Field label="Owner" value={lead.assignedTo} />
              <Field label="Email" value={<EmailLink email={lead.email} name={lead.contactName} leadId={lead.id} />} />
              <Field label="Phone" value={lead.phone && <a className="text-primary hover:underline" href={`tel:${lead.phone}`}>{lead.phone}</a>} />
              <Field label="Website" value={lead.website} />
              <Field label="Source" value={lead.source} />
              <Field label="Campaign" value={lead.campaign} />
              <Field label="Priority" value={lead.priority} />
              <Field label="Next step" value={lead.nextStep} />
              <Field label="Next follow-up" value={formatDateTime(lead.nextFollowUpAt)} />
              <Field label="Created" value={formatDateTime(lead.createdAt)} />
            </dl>
          </DetailSection>
          <DetailSection title="Notes">
            {activity.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading notes…</p>
            ) : notes.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nothing logged yet. Write one in{' '}
                <span className="font-medium text-foreground">Action needed</span> above — a call, an
                email, or any other context worth keeping.
              </p>
            ) : (
              <ActivityTimeline items={notes.slice(0, 8)} />
            )}
            {notes.length > 8 && (
              <button
                type="button"
                onClick={() => setTab('notes')}
                className="mt-3 text-[13px] font-medium text-primary hover:underline"
              >
                View all {notes.length} notes
              </button>
            )}
          </DetailSection>
          {/*
            Reopening a finished lead, and only that.

            The old "Manage lead" section held Archive, which was a second word
            for Dismiss and a second place to take a lead out of the queue —
            Archive at the bottom of the Overview, and the Action Center at the
            top. One decision may not have two controls, so Dismiss won: it
            lives beside the note that explains it.

            What is left is the way back, which the Action Center genuinely
            cannot offer, because its terminal buttons are hidden once a lead is
            finished. Reopening returns the lead to Active rather than New — it
            has been worked, and sending it back to the top of the queue as
            untouched would be a lie.
          */}
          {!isOpenCascadeStatus(lead.status) && (
            <DetailSection title="Reopen lead">
              <Button
                variant="outline"
                size="sm"
                disabled={reopenBusy}
                onClick={() => void reopen()}
              >
                Reopen lead
              </Button>
              <p className="mt-2 text-xs text-muted-foreground">
                This lead was {cascadeStatusLabel(lead.status).toLowerCase()}. Reopening returns it
                to the active list so it can be worked again. Nothing was deleted.
              </p>
            </DetailSection>
          )}
        </>}
        {tab === 'linked' && <>
          <DetailSection title="Contact">
            <div className="mb-3 flex justify-end"><LinkContactDialog leadId={lead.id} currentContactId={contact?.id} /></div>
            <RelatedRecordList empty="No contact is linked or matched to this lead." items={contact ? [{ id: contact.id, to: `/contacts/${contact.id}`, title: contact.fullName, subtitle: [contact.email, contact.phone].filter(Boolean).join(' · ') }] : []} />
          </DetailSection>
          <DetailSection title="Organization"><RelatedRecordList empty="No organization is linked to this lead." items={organization ? [{ id: organization.id, to: `/organizations/${organization.id}`, title: organization.name, subtitle: organization.industry }] : []} /></DetailSection>
          <LinkedIntakeRecords items={intake} />
        </>}
        {tab === 'notes' && <NotesPanel entityType="lead" entityId={lead.id} entityLabel={lead.title} activity={activity.data?.items ?? []} />}
        {tab === 'activity' && <DetailSection title="Activity and history">{activity.isError ? <p className="text-sm text-muted-foreground">Timeline unavailable — control plane not connected.</p> : <ActivityTimeline items={activity.data?.items ?? []} />}</DetailSection>}
      </DetailTabBody>
    </RecordDetailShell>
  );
}

/**
 * What counts as a note on a lead: an explicit note, or any recorded
 * interaction that carried note text. Mirrors NotesPanel so the Overview
 * readout and the Notes tab never disagree.
 */
function isNoteEntry(item: ActivityItem): boolean {
  if (item.action === 'record.note_added') return true;
  if (!item.action.includes('record_interaction')) return false;
  return Boolean(item.metadata?.description ?? item.metadata?.notes);
}
